import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Share2, Copy, Trash2, ExternalLink } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface ProjectShareDialogProps {
  projectId: string;
  projectName: string;
}

interface ProjectShare {
  id: string;
  share_token: string;
  permissions: 'view' | 'edit';
  description?: string;
  expires_at?: string;
  created_at: string;
}

export const ProjectShareDialog = ({ projectId, projectName }: ProjectShareDialogProps) => {
  const [open, setOpen] = useState(false);
  const [permissions, setPermissions] = useState<'view' | 'edit'>('view');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: shares = [], isLoading } = useQuery({
    queryKey: ['project-shares', projectId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('project-share-operations', {
        body: {
          operation: 'list',
          projectId,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data.data as ProjectShare[];
    },
    enabled: open,
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('project-share-operations', {
        body: {
          operation: 'create',
          projectId,
          permissions,
          description: description || null,
          expiresAt: expiresAt || null,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-shares', projectId] });
      setDescription('');
      setExpiresAt('');
      setPermissions('view');
      toast({
        title: "Enlace creado",
        description: "El enlace de compartir ha sido creado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteShareMutation = useMutation({
    mutationFn: async (shareId: string) => {
      const { data, error } = await supabase.functions.invoke('project-share-operations', {
        body: {
          operation: 'delete',
          shareId,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-shares', projectId] });
      toast({
        title: "Enlace eliminado",
        description: "El enlace de compartir ha sido eliminado",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const copyToClipboard = (token: string) => {
    const url = `${window.location.origin}/shared/project/${token}`;
    navigator.clipboard.writeText(url);
    toast({
      title: "Enlace copiado",
      description: "El enlace ha sido copiado al portapapeles",
    });
  };

  const openSharedLink = (token: string) => {
    const url = `${window.location.origin}/shared/project/${token}`;
    window.open(url, '_blank');
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await createShareMutation.mutateAsync();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="h-4 w-4 mr-2" />
          Compartir Proyecto
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compartir "{projectName}"</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create new share form */}
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="font-medium">Crear nuevo enlace</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="permissions">Permisos</Label>
                <Select value={permissions} onValueChange={(value) => setPermissions(value as 'view' | 'edit')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">Solo visualización</SelectItem>
                    <SelectItem value="edit">Visualización y edición</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expires">Fecha de expiración (opcional)</Label>
                <Input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción (opcional)</Label>
              <Textarea
                placeholder="Ej: Enlace para el cliente revisar progreso"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? "Creando..." : "Crear enlace"}
            </Button>
          </div>

          {/* Existing shares list */}
          <div className="space-y-4">
            <h3 className="font-medium">Enlaces existentes</h3>
            
            {isLoading ? (
              <div className="text-center py-4">Cargando enlaces...</div>
            ) : shares.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No hay enlaces compartidos para este proyecto
              </div>
            ) : (
              <div className="space-y-3">
                {shares.map((share) => (
                  <div key={share.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {share.permissions === 'view' ? 'Solo visualización' : 'Edición completa'}
                          </span>
                          {share.expires_at && (
                            <span className="text-sm text-muted-foreground">
                              Expira: {new Date(share.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {share.description && (
                          <p className="text-sm text-muted-foreground">{share.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Creado: {new Date(share.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(share.share_token)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSharedLink(share.share_token)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteShareMutation.mutate(share.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="p-2 bg-muted rounded text-sm font-mono break-all">
                      {window.location.origin}/shared/project/{share.share_token}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
